import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Activity,
  ArrowLeftRight,
  BarChart2,
  Coins,
  Cpu,
  Gem,
  KeyRound,
  LayoutDashboard,
  Layers,
  LogOut,
  ShieldAlert,
  User,
  UserCog,
  Users,
  Wallet,
  BookOpen,
  History,
  UserX,
  Landmark,
} from "lucide-react";
import AppSwitcher from "../../components/AppSwitcher";
import { NavItem } from "../../components/Sidebar";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "../../components/ui/sidebar";
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { logoutUser } from "../../slices/authSlice";
import { useCan, useOperator } from "./api";

const LOGIN = "/forex-admin/login";

// [path, icon, label, exact, permission]. An entry shows only when the
// operator's role holds its permission (025). Risk Reports and Client Book read
// the main backend, and the overview gates each of its panels, so none of
// those three asks for one.
const NAV = [
  ["Operations", [
    ["/forex-admin", LayoutDashboard, "Overview", true],
    ["/forex-admin/accounts", Wallet, "Accounts", false, "accounts.read"],
    ["/forex-admin/deleted-accounts", UserX, "Deleted accounts", false, "accounts.read"],
    ["/forex-admin/book", Activity, "Live Book", false, "accounts.read"],
    ["/forex-admin/reports", BarChart2, "Reports", false, "reports.read"],
    ["/forex-admin/finance", Landmark, "Finance", false, "finance.read"],
  ]],
  ["Risk", [
    ["/forex-admin/risk", ShieldAlert, "Risk Reports"],
    ["/forex-admin/clients", BookOpen, "Client Book"],
  ]],
  ["Money & users", [
    ["/forex-admin/transfers", ArrowLeftRight, "MT5 Transfers", false, "transfers.read"],
    ["/forex-admin/users", Users, "Users", false, "users.read"],
  ]],
  ["Copy trading", [
    ["/forex-admin/masters", Gem, "Masters", false, "masters.read"],
    ["/forex-admin/connections", Layers, "Connections", false, "connections.read"],
    ["/forex-admin/settlements", Coins, "Settlements", false, "settlements.read"],
    ["/forex-admin/copy-engine", Cpu, "Copy engine", false, "copy_engine.read"],
  ]],
  ["Access", [
    ["/forex-admin/roles", KeyRound, "Roles", false, ["roles.manage", "operators.manage"]],
    ["/forex-admin/operators", UserCog, "Operators", false, "operators.manage"],
    ["/forex-admin/audit", History, "Audit trail", false, "audit.read"],
  ]],
];

export function Blocked({ title, children }) {
  return (
    <div className="mx-auto mt-16 max-w-md rounded-xl border border-border/60 bg-white/[0.02] p-8 text-center">
      <ShieldAlert className="mx-auto h-8 w-8 text-amber-400" />
      <h1 className="mt-4 text-lg font-medium">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function TopBar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);

  const logout = () => {
    dispatch(logoutUser());
    toast.success("Logged out successfully");
    navigate(LOGIN);
  };

  return (
    <header className="flex h-14 items-center gap-3 border-b border-border bg-topbar px-3 sm:h-16 sm:px-4">
      <SidebarTrigger className="h-9 w-9 rounded-md border border-border/70 bg-white/[0.02] text-muted-foreground hover:text-foreground [&_svg]:h-4 [&_svg]:w-4" />
      <span className="flex-1 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
        Forex <span className="text-muted-foreground/30">/</span>{" "}
        <span className="text-foreground">Admin panel</span>
      </span>
      <AppSwitcher inApp />
      {user && (
        <span className="hidden max-w-[150px] truncate text-xs text-muted-foreground sm:inline">
          {user.email || user.name}
        </span>
      )}
      <button
        onClick={logout}
        className="flex items-center gap-2 rounded-md border border-border/70 bg-white/[0.02] px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground"
        title="Logout"
      >
        <LogOut className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Logout</span>
      </button>
    </header>
  );
}

// Forex Admin is its own app, like Gidex: same admin session, own sidebar, and
// on top of the login a forex grant, checked once here before any page loads.
export default function ForexShell({ children }) {
  const me = useOperator();
  const can = useCan();

  let content = children;
  // A 401 is already redirecting to the login (see sessionExpired in ./api).
  if (me.isLoading || me.error?.status === 401) content = <Skeleton className="h-40 w-full" />;
  else if (me.error?.status === 403)
    content = (
      <Blocked title="No forex access">
        Your admin account has not been granted access to Forex Admin. Ask for forex access, or
        switch to another panel from the top bar.
      </Blocked>
    );
  else if (me.error) content = <ErrorBanner error={me.error} onRetry={() => me.refetch()} />;

  return (
    <SidebarProvider defaultOpen={true}>
      <Sidebar className="border-r border-sidebar-border bg-[#0a0c10]">
        <SidebarHeader className="h-16 flex-row items-center gap-3 px-5">
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Ginox" className="h-9 w-9 shrink-0 object-contain" />
          <p className="font-serif text-[22px] italic leading-none text-sidebar-foreground">Forex</p>
        </SidebarHeader>
        <SidebarContent className="px-2 py-4">
          {me.isSuccess &&
            NAV.map(([label, items]) => [label, items.filter((i) => can(i[4]))])
              .filter(([, items]) => items.length > 0)
              .map(([label, items]) => (
                <SidebarGroup key={label} className="py-1">
                  <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">
                    {label}
                  </SidebarGroupLabel>
                  <SidebarGroupContent className="space-y-0.5">
                    {items.map(([to, icon, text, exact]) => (
                      <NavItem key={to} to={to} icon={icon} exact={exact}>
                        {text}
                      </NavItem>
                    ))}
                  </SidebarGroupContent>
                </SidebarGroup>
              ))}
          {me.isSuccess && (
            <p className="mt-auto flex items-center gap-2 px-3 pt-6 text-[11px] text-muted-foreground">
              <User className="h-3.5 w-3.5 shrink-0" />
              <span title={`Operator ${me.data.operatorId} · role ${me.data.role.id}`}>
                {me.data.name} · {me.data.role.name}
                <span className="block font-mono text-[10px] text-muted-foreground/70">{me.data.operatorId}</span>
              </span>
            </p>
          )}
        </SidebarContent>
      </Sidebar>
      <SidebarInset className="flex min-h-screen min-w-0 flex-col overflow-hidden bg-background">
        <TopBar />
        <main className="flex-1">
          <div className="mx-auto max-w-[1440px] px-5 py-6 sm:px-8 sm:py-8 md:px-10 md:py-10">
            {content}
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
